import { DEX_TOKEN } from "../../../config/dex-token";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiClient } from "../../../api/api-client";
import { nftSchema, nftRandomnessSchema, type BootstrapResponse } from "../../../api/schemas";
import type { NftReadView } from "../types";
import { errorMessage, sameAddress } from "../../../state/model-helpers";
import { selectedNftView } from "./view-mappers";
import { watchNftEvents } from "../../../wallet/watch-nft-events";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import { getAddress } from "viem";
import { ELYSIUM, elysiumBootstrapReasons, sameIdentity } from "../../../config/elysium";
import { readElysiumNft } from "../../../wallet/elysium-nft-read";
import { configuredElysiumRpcUrl } from "../../../wallet/elysium-rpc";
import { httpBatchReadProvider } from "../../../wallet/http-read-provider";
import { NftDisplayCache } from "../../../wallet/nft-display-cache";

interface Options {
  api: ApiClient;
  bootstrap: BootstrapResponse | null;
  sourceToken: string | null;
  contextKey: string;
  readContextKey(): string;
  provider?: Eip1193Provider | null;
  refreshRevision?: number;
}

// A bounded observation of the selected NFT only. This does not replace retained
// inventory snapshots, media provenance, ownership checks or write permissions.
export function useNftRead({ api, bootstrap, sourceToken, contextKey, readContextKey, provider, refreshRevision }: Options): {
  inspectNft(id: string | null): void; refreshNft(): void; nftRead: NftReadView | null;
} {
  const [selection, setSelection] = useState<{ context: string; tokenId: string } | null>(null);
  const [stored, setStored] = useState<{ key: string; cache: NftDisplayCache; value: NftReadView } | null>(null);
  const generation = useRef(0);
  const requestRefresh = useRef<() => void>(() => {});
  const refreshNft = useCallback(() => requestRefresh.current(), []);
  const [observation, setObservation] = useState<{ key: string; available: boolean } | null>(null);
  const tokenId = selection?.context === contextKey ? selection.tokenId : null;
  const collection = bootstrap?.contracts.collectionAddress;
  const chainId = bootstrap?.contracts.mintChainId;
  const admissionError = chainId === ELYSIUM.chainId && bootstrap ? elysiumBootstrapReasons(bootstrap).join(", ") : null;
  const rpcUrl = chainId === ELYSIUM.chainId ? configuredElysiumRpcUrl()?.href : undefined;
  const scope = JSON.stringify([contextKey, collection, chainId, sourceToken, rpcUrl, admissionError]);
  const display = useMemo(() => ({ scope, provider, cache: new NftDisplayCache(), last: null as { key: string; value: NftReadView } | null }), [scope, provider]);
  useEffect(() => () => display.cache.clear(), [display]);
  const key = JSON.stringify([contextKey, tokenId, collection, chainId, sourceToken]);
  const sourceSymbol = chainId === ELYSIUM.chainId && sameAddress(sourceToken, ELYSIUM.token) ? DEX_TOKEN.symbol :
    bootstrap?.tokens.find((token) => sameAddress(token.tokenAddress, sourceToken))?.symbol ?? "TOKEN";
  const inspectNft = useCallback((id: string | null) => {
    if (id !== null && (!/^(0|[1-9][0-9]{0,77})$/.test(id) || BigInt(id) >= 1n << 256n)) {
      throw new Error("Invalid NFT token ID");
    }
    setSelection(id === null ? null : { context: contextKey, tokenId: id });
  }, [contextKey]);

  useEffect(() => {
    if (tokenId === null || !collection || !chainId || !sourceToken) return;
    const abort = new AbortController();
    // A selection owns its HTTP queue and cancellation. Wallet fallback remains
    // EIP-1193; closing this detail never aborts unrelated application reads.
    const source = rpcUrl ? httpBatchReadProvider(new URL(rpcUrl), "ELYSIUM_NFT", abort.signal) : provider;
    const directProvider = source ? display.cache.provider(source, abort.signal) : null;
    const revision = ++generation.current;
    let pending = false;
    let invalidation = 0;
    let queued = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let nextReadAt = 0;
    let feeRequest: AbortController | null = null;
    const interval = chainId === ELYSIUM.chainId ? 30_000 : 12_000;
    const current = () => !abort.signal.aborted && revision === generation.current && contextKey === readContextKey();
    const publish = (value: NftReadView) => {
      if (!current()) return;
      if (chainId === ELYSIUM.chainId) display.last = { key, value };
      setStored({ key, cache: display.cache, value });
    };
    const empty: NftReadView = { tokenId, loading: false, status: null, dataStatus: null, randomness: null, error: null };
    const stale = (): NftReadView => ({ ...empty,
      ...(chainId === ELYSIUM.chainId && display.last?.key === key ? display.last.value : {}),
      ...(chainId === ELYSIUM.chainId && display.last?.key === key && display.last.value.historicalMintFee
        ? { historicalMintFeeStale: true } : {}),
      dataStatus: "STALE", loading: false, error: null });
    const cancelFee = () => {
      feeRequest?.abort();
      feeRequest = null;
    };
    const loadFee = () => {
      // Ordinary polling shares one pending request, including timeout/cooldown
      // waits. Explicit invalidation cancels it and starts a new fee generation.
      if (feeRequest || !current()) return;
      const request = new AbortController();
      feeRequest = request;
      const started = invalidation;
      const load = async () => {
        try {
          const history = await api.get(`/nfts/${tokenId}`, nftSchema, request.signal);
          if (!current() || request.signal.aborted || feeRequest !== request || started !== invalidation) return;
          // Read the latest chain observation here, never the one that started
          // this request: polling may have changed owner/reward/payout meanwhile.
          const latest = display.last?.key === key ? display.last.value : null;
          if (!latest?.detail || history.chainId !== chainId || history.tokenId !== tokenId ||
            !sameAddress(history.collectionAddress, collection) || !sameAddress(history.sourceToken, sourceToken) ||
            latest.detail.mintTxHash && history.transactions.mint &&
            !sameIdentity(latest.detail.mintTxHash, history.transactions.mint)) return;
          publish({ ...latest, historicalMintFeeStale: false, historicalMintFee: { chainId,
            collectionAddress: collection, tokenId, sourceToken, mintTxHash: history.transactions.mint,
            mintFeeWei: history.mintFeeWei ?? null } });
        } catch {
          // Keep saved fee evidence labelled stale. ApiClient retains its normal
          // validation, cancellation, cooldown and session-expiry handling.
        } finally {
          if (feeRequest === request) feeRequest = null;
        }
      };
      load().catch(() => console.error("NFT fee observation could not be published."));
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!current() || document.visibilityState === "hidden") return;
      timer = setTimeout(refresh, Math.max(0, nextReadAt - Date.now()));
    };
    const load = async () => {
      if (pending || !current()) return;
      pending = true;
      clearTimeout(timer);
      publish({ ...stale(), loading: true });
      const started = invalidation;
      try {
        if (chainId === ELYSIUM.chainId) {
          if (!directProvider || admissionError || !sameIdentity(sourceToken, ELYSIUM.token))
            throw new Error("ELYSIUM_NFT_ADMISSION_UNAVAILABLE");
          const result = await readElysiumNft(directProvider, tokenId, () => {
            if (!current()) throw new Error("NFT_CONTEXT_CHANGED");
            if (started !== invalidation) throw new Error("NFT_READ_INVALIDATED");
          }, { signal: abort.signal, displayCache: display.cache });
          if (!current() || started !== invalidation) return;
          failures = 0;
          const saved = display.last?.key === key ? display.last.value : null;
          const observation: NftReadView = { ...empty,
            ...(saved?.historicalMintFee ? { historicalMintFee: saved.historicalMintFee,
              historicalMintFeeStale: saved.historicalMintFeeStale === true } : {}),
            status: result.detail.status, dataStatus: "CURRENT", randomness: "FINALIZED",
            detail: result.detail, freshnessReasons: ["KNOWN_ID_ONLY_NOT_WALLET_INVENTORY", ...(result.mediaError ? [result.mediaError] : [])] };
          // Publish chain state immediately. Indexer latency/failure must not affect
          // ownership, payout, refresh errors or transaction eligibility.
          publish(observation);
          // A shared fee request may already have confirmed while RPC was
          // pending. Do not immediately duplicate that successful observation.
          if (!saved?.historicalMintFee || saved.historicalMintFeeStale) loadFee();
          return;
        }
        const results = await Promise.allSettled([
          api.get(`/nfts/${tokenId}`, nftSchema, abort.signal),
          api.get(`/nfts/${tokenId}/randomness`, nftRandomnessSchema, abort.signal),
        ]);
        const [detailResult, randomnessResult] = results;
        if (detailResult.status === "rejected") throw detailResult.reason;
        if (randomnessResult.status === "rejected") throw randomnessResult.reason;
        const detail = detailResult.value;
        const randomness = randomnessResult.value;
        if (detail.tokenId !== tokenId || randomness.tokenId !== tokenId || detail.chainId !== chainId ||
          !sameAddress(detail.collectionAddress, collection) || !sameAddress(detail.sourceToken, sourceToken)) {
          throw new Error("NFT response does not match the selected deployment and token");
        }
        if (started !== invalidation) return;
        failures = 0;
        publish({ ...empty, status: detail.status, dataStatus: detail.dataStatus,
          randomness: randomness.status, detail: selectedNftView(detail, sourceSymbol),
          stallReason: randomness.stallReason,
          freshnessReasons: [...randomness.mirrorFreshness.reasons,
            ...(!randomness.mirrorFreshness.current ? ["NFT_INDEXER_NOT_CURRENT"] : []),
            ...(detail.status !== randomness.onChainStatus ? ["NFT_READS_NOT_YET_ALIGNED"] : [])] });
      } catch (error) {
        if (current() && started === invalidation) {
          failures++;
          display.cache.clear();
          publish({ ...stale(), error: errorMessage(error) });
        }
      } finally {
        pending = false;
        nextReadAt = Date.now() + (failures ? Math.min(120_000, interval * 2 ** Math.min(failures - 1, 3)) * (1 + Math.random() * 0.2) : interval);
        if (queued && current()) { queued = false; refresh(); }
        else schedule();
      }
    };
    const refresh = () => { load().catch(() => console.error("NFT observation could not be published.")); };
    const invalidate = () => {
      if (!current()) return;
      invalidation++;
      cancelFee();
      display.cache.clear();
      publish({ ...stale(), loading: true });
      if (pending) queued = true;
      else refresh();
    };
    requestRefresh.current = invalidate;
    publish({ ...stale(), loading: true });
    if (document.visibilityState !== "hidden") refresh();
    const backgroundRefresh = () => {
      clearTimeout(timer);
      if (document.visibilityState !== "hidden") {
        if (Date.now() >= nextReadAt) refresh(); else schedule();
      }
    };
    document.addEventListener("visibilitychange", backgroundRefresh);
    return () => {
      abort.abort(); cancelFee(); clearTimeout(timer);
      document.removeEventListener("visibilitychange", backgroundRefresh);
      if (requestRefresh.current === invalidate) requestRefresh.current = () => {};
    };
  }, [api, tokenId, collection, chainId, sourceToken, sourceSymbol, contextKey, readContextKey, key, rpcUrl, provider, admissionError, display]);

  useEffect(() => { display.cache.clear(); refreshNft(); }, [refreshRevision, refreshNft, display]);
  useEffect(() => {
    // This observer itself polls multiple wallet RPCs. Elysium's direct detail
    // polling already reads owner/state at a verified head; avoid a second loop.
    if (!provider || tokenId === null || !collection || !chainId || chainId === ELYSIUM.chainId) return;
    return watchNftEvents(provider, { chainId, collection: getAddress(collection), tokenId: BigInt(tokenId) },
      refreshNft, (available) => setObservation({ key, available }));
  }, [provider, tokenId, collection, chainId, key, refreshNft]);

  return { inspectNft, refreshNft, nftRead: tokenId === null ? null : stored?.key === key && stored.cache === display.cache ?
    { ...stored.value, eventObservationAvailable: observation?.key === key && observation.available } :
    { tokenId, loading: true, status: null, dataStatus: null, randomness: null, error: null } };
}
